import { useEffect, useRef, useState, type ReactNode } from "react";

import { IconAlertTri, IconMore, IconSettings } from "@/lib/icons";
import type { WidgetSize } from "../../types";
import { buildProvenanceTitle } from "./format";

interface WidgetShellProps {
  title: string;
  /** Sublinha o eixo público do sinal (ex: "Entrada 1 — Estado"). */
  subtitle?: string | null;
  /** Conteúdo do corpo central do card. */
  children: ReactNode;
  /** Rodapé do card (timestamp + status). Use `<WidgetFooter ... />`. */
  footer?: ReactNode;
  /** Tom do card: ok (neutro), warn ou danger. */
  tone?: "ok" | "warn" | "danger";
  /** Estilo "silent" — sem snapshot recente. */
  silent?: boolean;
  /** Layout do corpo: row (default), col, ou sem classe extra. */
  bodyLayout?: "row" | "col";
  /** Tamanho do card. `md` é o default — não emite classe. */
  size?: WidgetSize;
  /**
   * Alarme visual: quando true E `tone === "danger"`, aplica `.w--alarm`
   * (borda animada + sombra pulsante). Para chamar a atenção do operador
   * sem precisar de som.
   */
  alarmEnabled?: boolean;
  /** Acessório à direita do header (substitui o botão `…` default). */
  accessory?: ReactNode;
  /** Callback do botão de configurar (engrenagem). */
  onConfigure?: () => void;
  /** Modo edição: mostra a alça extra de configuração. */
  editable?: boolean;
  /**
   * Quando definido E o card está em modo Visualização (`!editable`), o
   * botão `…` vira um menu com a ação "Gerar gráfico". Só faz sentido para
   * sinais numéricos (`v`/`acc`); o `SignalCard` decide quando passar.
   */
  onGenerateChart?: () => void;
}

/**
 * Casca compartilhada por todos os widgets visuais.
 *
 * Estrutura visual (vinda do contrato `docs/design-history/dashboard-operacional`):
 * - `.w-hd`: título + subtítulo + acessório/alert
 * - `.w-body`: corpo (`row` ou `col`)
 * - rodapé é passado em `footer` (use `<WidgetFooter />`)
 *
 * **Não** injeta "MAQUINA <id>" nem outro vocabulário técnico — esse era um
 * problema da versão anterior, removido nesta refatoração.
 */
export function WidgetShell({
  title,
  subtitle,
  children,
  footer,
  tone = "ok",
  silent = false,
  bodyLayout = "row",
  alarmEnabled = false,
  accessory,
  onConfigure,
  editable = false,
  onGenerateChart,
}: WidgetShellProps) {
  const toneClass = tone === "warn" ? "warn" : tone === "danger" ? "danger" : "";
  const silentClass = silent ? "silent" : "";
  // Tamanho padronizado (relatório 21/06): todos os cards uniformes. O `size`
  // segue na prop/modelo mas não emite mais classe — volta com os gráficos.
  const sizeClass = "";
  // Alarme só dispara quando o operador habilitou E o tom resolveu como
  // danger. Warning não dispara — é avisar sem assustar.
  const alarmClass = alarmEnabled && tone === "danger" ? "w--alarm" : "";
  const showAlert = tone === "warn" || tone === "danger";

  return (
    <article
      className={["w", toneClass, silentClass, sizeClass, alarmClass]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="w-hd">
        <div className="w-hd-l">
          <div className="w-title" title={title}>
            {title}
          </div>
          {subtitle ? (
            <div className="w-sub" title={subtitle}>
              {subtitle}
            </div>
          ) : null}
        </div>
        <div className="w-hd-r">
          {showAlert ? (
            <IconAlertTri size={13} style={{ color: "var(--warn-500)" }} />
          ) : null}
          {accessory ??
            (editable ? (
              <button
                type="button"
                className="iconbtn cfg"
                style={{ width: 24, height: 24 }}
                onClick={onConfigure}
                aria-label={`Configurar ${title}`}
                title={`Configurar ${title}`}
              >
                <IconSettings size={14} />
              </button>
            ) : onGenerateChart ? (
              <WidgetMenu title={title} onGenerateChart={onGenerateChart} />
            ) : (
              <button
                type="button"
                className="iconbtn cfg"
                style={{ width: 24, height: 24, borderColor: "transparent", opacity: 0.4 }}
                aria-label={`Opções de ${title}`}
                title="Disponível no modo Edição"
                disabled
              >
                <IconMore size={14} />
              </button>
            ))}
        </div>
      </div>

      <div className={["w-body", bodyLayout].filter(Boolean).join(" ")}>{children}</div>

      {footer}
    </article>
  );
}

interface WidgetFooterProps {
  /** Texto relativo da medição (ex: "há 3s"). Vazio mostra "—". */
  ts?: string | null;
  /**
   * Timestamp cru da medição. Alimenta o tooltip de proveniência (P0.3):
   * passa o mouse no "há Xs" e vê data/hora exata + origem do sinal.
   */
  measuredAt?: string | null;
  /** Tag de origem do sinal (ex.: "G0_P0_v") para o tooltip de proveniência. */
  source?: string;
  /** Tom do status mostrado. */
  status?: "ok" | "warn" | "danger" | "muted" | "info";
  /** Label do status (ex: "OK", "ALERTA", "SEM SINAL"). */
  statusLabel: string;
  /** Quando true, o ponto pulsante substitui o ponto estático. */
  animated?: boolean;
}

export function WidgetFooter({
  ts,
  measuredAt,
  source,
  status = "ok",
  statusLabel,
  animated,
}: WidgetFooterProps) {
  const provenanceTitle = buildProvenanceTitle(measuredAt, source);
  return (
    <div className="w-ft">
      <span className="ts" title={provenanceTitle}>
        <span className="clk" />
        {ts ?? "—"}
      </span>
      <span className={`badge ${status}`}>
        {animated ? <span className="led-anim" /> : <span className="dot" />}
        {statusLabel}
      </span>
    </div>
  );
}

/**
 * Menu `…` do card no modo Visualização. Popover leve com fechamento por
 * Escape e clique fora; o foco volta ao botão ao fechar pelo teclado. Hoje
 * tem só "Gerar gráfico", mas é o lugar natural de novas ações de leitura.
 * Exportado para o `HeroCard` reusar o mesmo popover.
 */
export function WidgetMenu({
  title,
  onGenerateChart,
  onHideChart,
}: {
  title: string;
  onGenerateChart?: () => void;
  /** Presente so nos cards que NASCERAM de um "Gerar grafico". */
  onHideChart?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="w-menu" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        className="iconbtn cfg"
        style={{ width: 24, height: 24 }}
        aria-label={`Opções de ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Opções"
        onClick={() => setOpen((current) => !current)}
      >
        <IconMore size={14} />
      </button>
      {open ? (
        <div className="w-menu__list" role="menu" aria-label={`Opções de ${title}`}>
          {onGenerateChart ? (
            <button
              type="button"
              role="menuitem"
              className="w-menu__item"
              onClick={() => {
                setOpen(false);
                onGenerateChart();
              }}
            >
              Gerar gráfico
            </button>
          ) : null}
          {onHideChart ? (
            <button
              type="button"
              role="menuitem"
              className="w-menu__item"
              onClick={() => {
                setOpen(false);
                onHideChart();
              }}
            >
              Ocultar gráfico
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
